import React from 'react';
import { Youtube } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * VideoEmbedRenderer - Render component for video-embed block
 */
const VideoEmbedRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const { url, caption } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  // Parse URL to get YouTube video ID
  const getVideoInfo = (videoUrl) => {
    if (!videoUrl) return null;
    const match = videoUrl.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? { id: match[1] } : null;
  };

  const videoInfo = getVideoInfo(url);

  // Show placeholder if no valid video
  if (!videoInfo) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto">
          <div
            className="aspect-video rounded-lg flex flex-col items-center justify-center"
            style={{
              backgroundColor: styles.cardBackground,
              border: `1px solid ${styles.borderColor}`,
              color: styles.mutedColor,
            }}
          >
            <Youtube size={48} className="mb-2" />
            <p className="text-sm">No video URL provided</p>
          </div>
        </div>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoInfo.id}?rel=0`;

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Video Container */}
        <div className="aspect-video rounded-lg overflow-hidden bg-black">
          <iframe
            src={embedUrl}
            title="Embedded video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>

        {/* Caption */}
        {caption && (
          <p
            className="mt-3 sm:mt-4 text-center text-sm sm:text-base"
            style={{ color: styles.mutedColor }}
          >
            {caption}
          </p>
        )}
      </div>
    </div>
  );
};

export default VideoEmbedRenderer;
